/**
 * Client frequency capping for the training-alerts contextual nudge.
 * Persists in localStorage; session visit bumps use sessionStorage.
 */

export const TRAINING_ALERTS_NUDGE_STORAGE_KEY = 'apt_training_alerts_nudge_v1';
export const TRAINING_ALERTS_NUDGE_VISIT_BUMPED_KEY =
  'apt_training_alerts_nudge_visit_bumped';
export const TRAINING_ALERTS_NUDGE_SHOWN_SESSION_KEY =
  'apt_training_alerts_nudge_shown_session';

export const TRAINING_ALERTS_NUDGE_SOFT_SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;
export const TRAINING_ALERTS_NUDGE_SOFT_SESSIONS_REQUIRED = 3;
export const TRAINING_ALERTS_NUDGE_MAX_LIFETIME_SHOWS = 3;

export type TrainingAlertsNudgeState = {
  shownCount: number;
  softDismissedAt: number | null;
  sessionsSinceSoftDismiss: number;
  hardOptOut: boolean;
  subscribed: boolean;
};

export const DEFAULT_TRAINING_ALERTS_NUDGE_STATE: TrainingAlertsNudgeState = {
  shownCount: 0,
  softDismissedAt: null,
  sessionsSinceSoftDismiss: 0,
  hardOptOut: false,
  subscribed: false,
};

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function parseState(raw: string | null): TrainingAlertsNudgeState {
  if (!raw) {
    return { ...DEFAULT_TRAINING_ALERTS_NUDGE_STATE };
  }
  try {
    const parsed = JSON.parse(raw) as Partial<TrainingAlertsNudgeState>;
    return {
      shownCount:
        typeof parsed.shownCount === 'number' && parsed.shownCount >= 0
          ? Math.floor(parsed.shownCount)
          : 0,
      softDismissedAt:
        typeof parsed.softDismissedAt === 'number' ? parsed.softDismissedAt : null,
      sessionsSinceSoftDismiss:
        typeof parsed.sessionsSinceSoftDismiss === 'number' &&
        parsed.sessionsSinceSoftDismiss >= 0
          ? Math.floor(parsed.sessionsSinceSoftDismiss)
          : 0,
      hardOptOut: parsed.hardOptOut === true,
      subscribed: parsed.subscribed === true,
    };
  } catch {
    return { ...DEFAULT_TRAINING_ALERTS_NUDGE_STATE };
  }
}

export function readTrainingAlertsNudgeState(
  storage?: StorageLike | null,
): TrainingAlertsNudgeState {
  if (!storage) {
    return { ...DEFAULT_TRAINING_ALERTS_NUDGE_STATE };
  }
  try {
    return parseState(storage.getItem(TRAINING_ALERTS_NUDGE_STORAGE_KEY));
  } catch {
    return { ...DEFAULT_TRAINING_ALERTS_NUDGE_STATE };
  }
}

export function writeTrainingAlertsNudgeState(
  state: TrainingAlertsNudgeState,
  storage?: StorageLike | null,
): void {
  if (!storage) {
    return;
  }
  try {
    storage.setItem(TRAINING_ALERTS_NUDGE_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Quota / private mode — ignore
  }
}

/**
 * Increment sessionsSinceSoftDismiss once per browser session when a soft
 * dismiss is active. No-op if never soft-dismissed or already bumped this session.
 */
export function bumpSessionVisit(
  localStorage?: StorageLike | null,
  sessionStorage?: StorageLike | null,
): TrainingAlertsNudgeState {
  const state = readTrainingAlertsNudgeState(localStorage);
  if (!sessionStorage) {
    return state;
  }
  try {
    if (sessionStorage.getItem(TRAINING_ALERTS_NUDGE_VISIT_BUMPED_KEY) === '1') {
      return state;
    }
    sessionStorage.setItem(TRAINING_ALERTS_NUDGE_VISIT_BUMPED_KEY, '1');
  } catch {
    return state;
  }

  if (state.softDismissedAt == null) {
    return state;
  }

  const next: TrainingAlertsNudgeState = {
    ...state,
    sessionsSinceSoftDismiss: state.sessionsSinceSoftDismiss + 1,
  };
  writeTrainingAlertsNudgeState(next, localStorage);
  return next;
}

export function wasNudgeShownThisSession(
  sessionStorage?: StorageLike | null,
): boolean {
  if (!sessionStorage) {
    return false;
  }
  try {
    return sessionStorage.getItem(TRAINING_ALERTS_NUDGE_SHOWN_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

export function markNudgeShownThisSession(
  sessionStorage?: StorageLike | null,
): void {
  if (!sessionStorage) {
    return;
  }
  try {
    sessionStorage.setItem(TRAINING_ALERTS_NUDGE_SHOWN_SESSION_KEY, '1');
  } catch {
    // ignore
  }
}

export function canShowNudge(
  state: TrainingAlertsNudgeState,
  nowMs: number = Date.now(),
  options?: { shownThisSession?: boolean },
): boolean {
  if (options?.shownThisSession) {
    return false;
  }
  if (state.hardOptOut || state.subscribed) {
    return false;
  }
  if (state.shownCount >= TRAINING_ALERTS_NUDGE_MAX_LIFETIME_SHOWS) {
    return false;
  }
  if (state.softDismissedAt != null) {
    const elapsed = nowMs - state.softDismissedAt;
    if (elapsed < TRAINING_ALERTS_NUDGE_SOFT_SNOOZE_MS) {
      return false;
    }
    if (
      state.sessionsSinceSoftDismiss < TRAINING_ALERTS_NUDGE_SOFT_SESSIONS_REQUIRED
    ) {
      return false;
    }
  }
  return true;
}

export function recordShown(
  localStorage?: StorageLike | null,
  sessionStorage?: StorageLike | null,
): TrainingAlertsNudgeState {
  const state = readTrainingAlertsNudgeState(localStorage);
  const next: TrainingAlertsNudgeState = {
    ...state,
    shownCount: state.shownCount + 1,
    softDismissedAt: null,
    sessionsSinceSoftDismiss: 0,
  };
  writeTrainingAlertsNudgeState(next, localStorage);
  markNudgeShownThisSession(sessionStorage);
  return next;
}

/**
 * After a soft dismiss in the same session where the visit was already bumped,
 * count this session toward the soft-retry threshold so users are not forced
 * through an extra empty session before the 3-session clock starts.
 */
export function recordSoftDismiss(
  localStorage?: StorageLike | null,
  nowMs: number = Date.now(),
  sessionStorage?: StorageLike | null,
): TrainingAlertsNudgeState {
  const state = readTrainingAlertsNudgeState(localStorage);
  let sessionsSinceSoftDismiss = 0;
  if (sessionStorage) {
    try {
      if (sessionStorage.getItem(TRAINING_ALERTS_NUDGE_VISIT_BUMPED_KEY) === '1') {
        sessionsSinceSoftDismiss = 1;
      }
    } catch {
      // ignore
    }
  }
  const next: TrainingAlertsNudgeState = {
    ...state,
    softDismissedAt: nowMs,
    sessionsSinceSoftDismiss,
  };
  writeTrainingAlertsNudgeState(next, localStorage);
  return next;
}

export function recordHardOptOut(
  localStorage?: StorageLike | null,
): TrainingAlertsNudgeState {
  const state = readTrainingAlertsNudgeState(localStorage);
  const next: TrainingAlertsNudgeState = {
    ...state,
    hardOptOut: true,
  };
  writeTrainingAlertsNudgeState(next, localStorage);
  return next;
}

export function recordSubscribed(
  localStorage?: StorageLike | null,
): TrainingAlertsNudgeState {
  const state = readTrainingAlertsNudgeState(localStorage);
  const next: TrainingAlertsNudgeState = {
    ...state,
    subscribed: true,
  };
  writeTrainingAlertsNudgeState(next, localStorage);
  return next;
}
